import { BarChart3 } from 'lucide-react';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../../../components/ui/radio-group';
import { useAppStore } from '../../../../store/appStore';
import type { CrashReportMode } from '../../../crash-dialog/types/crashTypes';

export function PrivacySettingsTab() {
  const analyticsEnabled = useAppStore((state) => state.analyticsEnabled);
  const setAnalyticsEnabled = useAppStore((state) => state.setAnalyticsEnabled);
  const crashReportMode = useAppStore((state) => state.crashReportMode);
  const setCrashReportMode = useAppStore((state) => state.setCrashReportMode);

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="text-sm font-medium">Usage Analytics</div>

        <div className="flex items-center justify-between rounded-md border border-border bg-background p-4">
          <div className="flex items-center gap-3">
            <BarChart3 className="h-5 w-5 text-muted-foreground" />
            <div className="grid gap-0.5">
              <Label htmlFor="analytics-enabled" className="cursor-pointer">
                Send Anonymous Usage Data
              </Label>
              <div className="text-xs text-muted-foreground">
                Help improve Jam SQL by sharing anonymous usage statistics. Crash
                and diagnostic reporting behavior is controlled separately below.
              </div>
            </div>
          </div>
          <Switch
            id="analytics-enabled"
            checked={analyticsEnabled}
            onCheckedChange={setAnalyticsEnabled}
            data-testid="analytics-enabled-toggle"
          />
        </div>

        <div className="rounded-md border border-border bg-background p-4">
          <div className="mb-3 text-sm font-medium">Crash and diagnostic reports</div>
          <RadioGroup
            value={crashReportMode}
            onValueChange={(value) => setCrashReportMode(value as CrashReportMode)}
            data-testid="crash-report-mode"
          >
            <div className="flex items-start gap-3">
              <RadioGroupItem value="ask" id="crash-mode-ask" data-testid="crash-mode-ask" />
              <div className="grid gap-0.5">
                <Label htmlFor="crash-mode-ask" className="cursor-pointer">
                  Ask me before sending a report
                </Label>
                <div className="text-xs text-muted-foreground">
                  Default. A dialog will show you what would be sent.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <RadioGroupItem value="always" id="crash-mode-always" data-testid="crash-mode-always" />
              <div className="grid gap-0.5">
                <Label htmlFor="crash-mode-always" className="cursor-pointer">
                  Always send automatically
                </Label>
                <div className="text-xs text-muted-foreground">
                  No dialog, just send. Useful for early-access users helping us ship fixes.
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <RadioGroupItem value="never" id="crash-mode-never" data-testid="crash-mode-never" />
              <div className="grid gap-0.5">
                <Label htmlFor="crash-mode-never" className="cursor-pointer">
                  Never send
                </Label>
                <div className="text-xs text-muted-foreground">
                  Don&apos;t show the dialog or send any crash report.
                </div>
              </div>
            </div>
          </RadioGroup>
        </div>

        <div className="rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
          <p className="mb-2 font-medium">What we collect:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>Feature usage (which tools you use)</li>
            <li>Sanitized crash diagnostics (error category/message and basic app state)</li>
            <li>App version and operating system</li>
            <li>Subscription type</li>
          </ul>
          <p className="mt-2 font-medium">What we never collect:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>SQL queries or database content</li>
            <li>Server names, connection strings, or file paths</li>
            <li>Personal or identifiable information</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
